/**
 * API Client
 * HTTP client with JWT token injection, error handling, and request/response interceptors
 */

import type {
  LoginRequest,
  LoginResponse,
  Policy,
  PolicyListParams,
  PolicyListResponse,
  CreatePolicyRequest,
  Finding,
  FindingListParams,
  FindingListResponse,
  CreateFindingRequest,
  Exception,
  ExceptionListParams,
  ExceptionListResponse,
  GrantExceptionRequest,
  UpdateExceptionRequest,
  ComplianceReportParams,
  ComplianceReport,
  ExceptionPrecedent,
  Subjectivity,
  SubjectivityListParams,
  SubjectivityListResponse,
  CreateSubjectivityRequest,
  UpdateSubjectivityRequest,
  ExpiringSubjectivitiesParams,
  ExpiringSubjectivitiesResponse,
  SubmitEvidenceRequest,
  SatisfySubjectivityRequest,
  WaiveSubjectivityRequest,
  SubjectivityActionResponse,
  SubjectivityCondition,
  ValidateSecureLinkResponse,
  UploadPhotoResponse,
  DashboardStats,
  PortfolioDashboardStats,
  FireZone,
  APIError,
  PhotoType,
  Photo,
  PhotoListParams,
  PhotoListResponse,
  FireUpdatesResponse,
  FireUpdatesSummaryResponse,
  SecureLink,
  SecureLinkResponse,
  RequestInspectionRequest,
  PortfolioAnalytics,
  PortfolioAnalyticsParams,
  DocumentListParams,
  DocumentListResponse,
  DocumentDetail,
  DocumentSearchParams,
  DocumentSearchResponse,
  DocumentUploadResponse,
  MitigationSummaryResponse,
  MitigationListParams,
  MitigationListResponse,
  SubjectivityCountByPolicy,
} from '../types/api';

import type {
  HistoricalFireEvent,
  HistoricalFirePerimeter,
  HistoricalFireHotspot,
  HistoricalTimelineEvent,
  HistoricalFiresParams,
  HistoricalPerimetersParams,
  HistoricalHotspotsParams,
  PaginatedResponse as HistoricalPaginatedResponse,
} from '../types/wildfire';

// === WORKER C: Enhanced layer types ===
import type {
  FireSeverityResponse,
  FireEvacuationResponse,
  FireLossResponse,
  FireAnalysisResponse,
} from '../types/layers';
// === END WORKER C ===

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** Build a full API URL for a given endpoint path */
export function getApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}

/**
 * Derive wildfire service base URL from the Bacchus API base.
 * In production the base is e.g. "https://…/v1/bacchus" and we swap the
 * service segment.  In dev/test the base is "/api" so we append the path.
 */
function getWildfireBaseUrl(): string {
  if (/\/v1\/bacchus/.test(API_BASE_URL)) {
    return API_BASE_URL.replace(/\/v1\/bacchus.*$/, '/v1/wildfire');
  }
  return `${API_BASE_URL}/v1/wildfire`;
}

const TOKEN_KEY = 'bacchus_jwt_token';
const REFRESH_TOKEN_KEY = 'bacchus_refresh_token';

// ============================================================================
// 403 Forbidden Banner (non-blocking, deduplicated)
// ============================================================================

const FORBIDDEN_BANNER_ID = 'api-client-forbidden-banner';

/**
 * Show a non-blocking banner for 403 errors. Deduplicated: only one banner
 * is shown at a time, regardless of how many parallel 403 responses arrive.
 * This replaces the previous window.alert() which caused an infinite loop
 * when multiple dashboard endpoints returned 403 simultaneously.
 */
function showForbiddenBanner(message: string): void {
  if (typeof document === 'undefined') return;
  // Deduplicate — if banner already exists, don't create another
  if (document.getElementById(FORBIDDEN_BANNER_ID)) return;

  const banner = document.createElement('div');
  banner.id = FORBIDDEN_BANNER_ID;
  banner.setAttribute('role', 'alert');
  banner.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:99999',
    'padding:12px 16px', 'background:#FEF2F2', 'border-bottom:2px solid #EF4444',
    'color:#991B1B', 'font-family:system-ui,sans-serif', 'font-size:14px',
    'display:flex', 'align-items:center', 'justify-content:space-between',
  ].join(';');
  banner.innerHTML = `
    <span><strong>Access denied:</strong> ${message.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>
    <button style="background:none;border:none;font-size:18px;cursor:pointer;color:#991B1B;padding:0 4px"
            aria-label="Dismiss">&times;</button>
  `;
  banner.querySelector('button')?.addEventListener('click', () => banner.remove());

  // Auto-dismiss after 8 seconds
  document.body.appendChild(banner);
  setTimeout(() => banner.remove(), 8000);
}

// ============================================================================
// Token Management
// ============================================================================

export const tokenManager = {
  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  },

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(TOKEN_KEY, token);
  },

  removeToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  removeRefreshToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  clearAll(): void {
    this.removeToken();
    this.removeRefreshToken();
  },

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Check if token is expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() < exp;
    } catch {
      return false;
    }
  },
};

// ============================================================================
// Error Handling
// ============================================================================

export class APIClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'APIClientError';
  }

  static fromResponse(error: APIError): APIClientError {
    return new APIClientError(error.message, error.status, error.code, error.details);
  }
}

// ============================================================================
// HTTP Client
// ============================================================================

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  skipAuth?: boolean;
  onUploadProgress?: (event: { loaded: number; total: number }) => void;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, skipAuth, onUploadProgress, ...fetchOptions } = options;

  // Build URL with query params
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Build headers
  const headers = new Headers(fetchOptions.headers);

  // Add JWT token if authenticated
  if (!skipAuth) {
    const token = tokenManager.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Set content type for JSON bodies
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // Make request
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // Handle non-OK responses
  if (!response.ok) {
    let errorData: APIError;
    try {
      const raw: Record<string, any> = await response.json();
      // Backend returns { error: 'text' } but APIError expects { message: 'text' }
      errorData = {
        code: raw.code || 'API_ERROR',
        message: raw.message || raw.error || `HTTP ${response.status}: ${response.statusText}`,
        status: raw.status || response.status,
        details: raw.details,
      };
    } catch {
      errorData = {
        code: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      };
    }

    // Handle 401 - Token expired or invalid
    if (response.status === 401 && !skipAuth) {
      if (import.meta.env.DEV) console.error('[APIClient] 401 Unauthorized - clearing tokens and redirecting to login');
      tokenManager.clearAll();
      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    // Handle 403 - Forbidden (insufficient permissions)
    // NOTE: Do NOT clear tokens or redirect on 403. The user is authenticated
    // but lacks permission for this specific resource. Clearing tokens would
    // break parallel requests and cause an infinite redirect loop.
    if (response.status === 403 && !skipAuth) {
      import.meta.env.DEV && console.error('[APIClient] 403 Forbidden - user lacks required permissions');
      import.meta.env.DEV && console.error('[APIClient] Error details:', errorData);

      if (import.meta.env.DEV) {
        const token = tokenManager.getToken();
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            import.meta.env.DEV && console.error('[APIClient] Current user role:', payload.role);
            import.meta.env.DEV && console.error('[APIClient] Current user email:', payload.email);
          } catch (e) {
            import.meta.env.DEV && console.error('[APIClient] Failed to decode token:', e);
          }
        }
      }

      // Show non-blocking banner (deduplicated — only one banner at a time)
      showForbiddenBanner(errorData.message || 'You do not have permission to access this resource.');
    }

    throw APIClientError.fromResponse(errorData);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  return response.json();
}

// Convenience methods
const get = <T>(endpoint: string, options?: RequestOptions) =>
  request<T>(endpoint, { ...options, method: 'GET' });

const post = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
  request<T>(endpoint, {
    ...options,
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

const put = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
  request<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

const patch = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
  request<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
  });

const del = <T>(endpoint: string, options?: RequestOptions) =>
  request<T>(endpoint, { ...options, method: 'DELETE' });

// ============================================================================
// API Methods
// ============================================================================

// ============================================================================
// Building Polygon Types
// ============================================================================

export type BuildingSource = 'manual_draw' | 'ms_footprint' | 'structure_sync';
export type BuildingStatus = 'pending' | 'approved' | 'rejected';
export type BuildingType =
  | 'barrel_hall' | 'tasting_room' | 'processing' | 'residence'
  | 'garage' | 'detached_garage' | 'barn' | 'pump_house' | 'other'
  | 'barrel_caves' | 'winery_production';

export interface BuildingPolygonResponse {
  id: string;
  property_id: string | null;
  geometry: string;
  height: number;
  min_height: number;
  source: BuildingSource;
  status: BuildingStatus;
  name: string | null;
  building_type: BuildingType | null;
  ms_footprint_id: string | null;
  gps_confirmation_lat: number | null;
  gps_confirmation_lon: number | null;
  min_lon: number;
  max_lon: number;
  min_lat: number;
  max_lat: number;
  submitted_by: string | null;
  rejected_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type BuildingPhotoSource = 'generated_hero' | 'user_upload' | 'inspection';

export interface BuildingPhotoResponse {
  id: string;
  building_id: string;
  photo_url: string;
  thumbnail_url: string | null;
  photo_source: BuildingPhotoSource;
  author: string | null;
  gps_latitude: number | null;
  gps_longitude: number | null;
  captured_at: string;
  created_at: string;
  is_current: number;
  compass_heading: number | null;
  wall_index: number | null;
  wall_label: string | null;
  wall_confidence: 'high' | 'medium' | 'low' | null;
  wall_method: 'ray-cast' | 'proximity' | null;
  pin_longitude: number | null;
  pin_latitude: number | null;
}

export interface BuildingSubmitRequest {
  propertyId?: string;
  geometry: string;
  height?: number;
  minHeight?: number;
  name?: string;
  buildingType?: BuildingType;
  msFootprintId?: string;
  gpsConfirmation?: { lat: number; lon: number };
}

export interface BuildingBboxParams {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
  status?: BuildingStatus;
  propertyId?: string;
}

export interface BuildingUpdateRequest {
  geometry?: string;
  height?: number;
  minHeight?: number;
  name?: string;
  buildingType?: BuildingType;
}

export interface BuildingReviewRequest {
  status: 'approved' | 'rejected';
  reason?: string;
}

export interface ImportPolicyRow {
  insuredName: string;
  propertyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  tiv: number;
  email?: string;
  phone?: string;
}

export interface ImportPoliciesRequest {
  policies: ImportPolicyRow[];
}

export interface ImportPoliciesResponse {
  imported: number;
  errors: { row: number; error: string }[];
  importBatchId: string;
  policies: { id: string; insuredName: string }[];
}

export interface BuildingCompletenessResponse {
  propertyId: string;
  mapped: number;
  approved: number;
  pending: number;
  rejected: number;
  percentage: number;
  allApproved: boolean;
  unlockedFeatures: string[];
}

export interface BuildingListParams {
  page?: number;
  limit?: number;
  status?: BuildingStatus;
  buildingType?: BuildingType;
  propertyId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface BuildingListResponse {
  buildings: BuildingPolygonResponse[];
  total: number;
  page: number;
  totalPages: number;
}

export interface AuditTrailEntry {
  id: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  resource_type: string;
  resource_id: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface BatchSubmitBuildingItem {
  geometry: string;
  height?: number;
  minHeight?: number;
  name?: string;
  buildingType?: string;
  msFootprintId?: string;
}

export interface BatchSubmitBuildingRequest {
  propertyId: string;
  buildings: BatchSubmitBuildingItem[];
}

export interface BatchSubmitBuildingResponse {
  created: string[];
  skipped: number;
  total: number;
}

export interface BatchReviewBuildingRequest {
  buildingIds: string[];
  status: 'approved' | 'rejected';
  reason?: string;
}

export interface BatchReviewBuildingResponse {
  reviewed: string[];
  selfReviewBlocked: string[];
  wrongStatus: string[];
  notFound: string[];
  total: number;
}

export interface DispatchQueueItem {
  dispatchId: string;
  policyId: string;
  inspectionId: string;
  status: string;
  brokerReviewStatus: string;
  reportUrl: string | null;
  captureUrl: string | null;
  completedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  policy: {
    name: string | null;
    address: string | null;
    score: number | null;
    grade: string | null;
  };
}

/**
 * Convert PortfolioFiltersRequest to the flat query-string shape the
 * /v1/admin/portfolio/* endpoints accept (Zod-coerced server-side).
 */
function portfolioFiltersToParams(
  filters?: import('../types/portfolio').PortfolioFiltersRequest
): Record<string, string | number | boolean | undefined> {
  if (!filters) return {};
  return {
    wineryAccountId: filters.wineryAccountId,
    wineryAccountIds: filters.wineryAccountIds?.length
      ? filters.wineryAccountIds.join(',')
      : undefined,
    fhszTier: filters.fhszTier,
    hciScoreMin: filters.hciScoreMin,
    hciScoreMax: filters.hciScoreMax,
    dsScoreMin: filters.dsScoreMin,
    dsScoreMax: filters.dsScoreMax,
    inspectionStatus: filters.inspectionStatus,
    search: filters.search,
    includeOrphans: filters.includeOrphans,
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const api = {
  // --------------------------------------------------------------------------
  // Authentication
  // --------------------------------------------------------------------------
  auth: {
    login: (data: LoginRequest) =>
      post<LoginResponse>('/auth/login', data, { skipAuth: true }),

    logout: () => post<void>('/auth/logout'),

    refresh: () =>
      post<{ token: string }>('/auth/refresh', {
        refreshToken: tokenManager.getRefreshToken(),
      }),

    me: () => get<LoginResponse['user']>('/auth/me'),

    forgotPassword: (email: string) =>
      post<{ message: string; token?: string }>('/auth/forgot-password', { email }, { skipAuth: true }),

    resetPassword: (token: string, newPassword: string) =>
      post<{ success: boolean; message: string }>('/auth/reset-password', { token, newPassword }, { skipAuth: true }),

    validateResetToken: (token: string) =>
      get<{ valid: boolean }>(`/auth/reset-password/${token}`, { skipAuth: true }),

    validateActivationToken: (token: string) =>
      get<{ valid: boolean; email?: string; role?: string; displayName?: string | null; error?: string }>(
        `/auth/activate/${token}`, { skipAuth: true }
      ),

    activate: (token: string, password: string) =>
      post<{ success: boolean; message: string }>('/auth/activate', { token, password }, { skipAuth: true }),

    // --- Gated Viral Identity: Signup & OIDC ---

    validateSignupToken: (token: string) =>
      get<import('../types/identity').SignupTokenValidation>(
        `/auth/signup/${token}`, { skipAuth: true },
      ),

    signup: (data: import('../types/identity').SignupRequest) =>
      post<import('../types/identity').SignupResponse>(
        '/auth/signup', data, { skipAuth: true },
      ),

    refreshOIDC: (refreshToken: string) =>
      post<{ token: string; refreshToken: string }>(
        '/oauth/token',
        { grant_type: 'refresh_token', refresh_token: refreshToken },
        { skipAuth: true },
      ),

    validateDomain: (email: string) =>
      get<import('../types/identity').DomainValidationResult>(
        '/share/validate-domain',
        { params: { email } },
      ),
  },

  // --------------------------------------------------------------------------
  // Policies
  // --------------------------------------------------------------------------
  policies: {
    list: (params?: PolicyListParams) =>
      get<PolicyListResponse>('/policies', { params: params as Record<string, string | number | boolean | undefined> }),

    get: (id: string) => get<Policy>(`/policies/${id}`),

    create: (data: CreatePolicyRequest) => post<Policy>('/policies', data),

    update: (id: string, data: Partial<CreatePolicyRequest>) =>
      patch<Policy>(`/policies/${id}`, data),

    delete: (id: string) => del<void>(`/policies/${id}`),

    getByFireZone: (fireZoneId: string) =>
      get<Policy[]>(`/policies/fire-zone/${fireZoneId}`),

    setHeroImage: (policyId: string, photoUrl: string) =>
      put<{ message: string; heroImageUrl: string }>(`/policies/${policyId}/hero-image`, { photoUrl }),

    getEvidencePacket: (policyId: string) =>
      get<import('../types/api').EvidencePacketResponse>(`/policies/${policyId}/evidence-packet`),

    getInspectionAttributes: (policyId: string) =>
      get<import('../types/api').InspectionAttributesResponse>(`/policies/${policyId}/inspection-attributes`),

    getPortfolio: (policyId: string) =>
      get<{ parent: Policy; children: Policy[]; stats: { childCount: number; totalTiv: number; totalFindings: number; statusBreakdown: Record<string, number> } }>(`/policies/${policyId}/portfolio`),

    importCSV: (data: ImportPoliciesRequest) =>
      post<ImportPoliciesResponse>('/policies/import', data),
  },

  // --------------------------------------------------------------------------
  // Findings
  // --------------------------------------------------------------------------
  findings: {
    list: (params?: FindingListParams) =>
      get<FindingListResponse>('/findings', { params: params as Record<string, string | number | boolean | undefined> }),

    get: (id: string) => get<Finding>(`/findings/${id}`),

    create: (data: CreateFindingRequest) => {
      // Transform camelCase to snake_case for backend
      const transformed = {
        id: crypto.randomUUID(), // Generate UUID
        policy_id: data.policyId,
        type: data.type,
        title: data.title,
        description: data.description,
        severity: data.severity,
        photo_urls: data.photoUrls || [],
        status: data.status || 'open',
      };
      return post<Finding>('/findings', transformed);
    },

    update: (id: string, data: Partial<CreateFindingRequest>) =>
      patch<Finding>(`/findings/${id}`, data),

    delete: (id: string) => del<void>(`/findings/${id}`),

    getByPolicy: async (policyId: string) => {
      const response = await get<any>('/findings', { params: { policy_id: policyId } });
      return response.data || [];
    },

    getAuditTrail: (id: string) =>
      get<{ entries: import('../types/api').AuditEntry[] }>(`/findings/${id}/audit-trail`),

    getEvidence: (id: string) =>
      get<{ photos: import('../types/api').EvidencePhoto[] }>(`/findings/${id}/evidence`),

    uploadEvidence: (id: string, data: { photo_ids?: string[]; source?: string }) =>
      post<any>(`/findings/${id}/evidence`, data),

    transitionStatus: (id: string, status: string) =>
      patch<any>(`/findings/${id}/status`, { status }),

    getException: (id: string) =>
      get<{ exception: import('../types/api').Exception | null }>(`/findings/${id}/exception`),
  },

  // --------------------------------------------------------------------------
  // Exceptions
  // --------------------------------------------------------------------------
  exceptions: {
    list: (params?: ExceptionListParams) =>
      get<ExceptionListResponse>('/exceptions', { params: params as Record<string, string | number | boolean | undefined> }),

    get: (id: string) => get<Exception>(`/exceptions/${id}`),

    grant: (data: GrantExceptionRequest) =>
      post<Exception>('/exceptions', data),

    update: (id: string, data: UpdateExceptionRequest) =>
      put<Exception>(`/exceptions/${id}`, data),

    revoke: (id: string) => del<void>(`/exceptions/${id}`),

    getExpiring: (days: number = 30) =>
      get<Exception[]>(`/exceptions/expiring-soon?days=${days}`),

    getComplianceReport: (params?: ComplianceReportParams) =>
      get<ComplianceReport>('/exceptions/compliance-report', {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    getPrecedents: (findingType: string, severity: string) =>
      get<ExceptionPrecedent>('/exceptions/precedents', {
        params: { finding_type: findingType, severity },
      }),
  },

  // --------------------------------------------------------------------------
  // Subjectivities
  // --------------------------------------------------------------------------
  subjectivities: {
    list: (params?: SubjectivityListParams) =>
      get<SubjectivityListResponse>('/subjectivities', { params: params as Record<string, string | number | boolean | undefined> }),

    get: (id: string) => get<Subjectivity>(`/subjectivities/${id}`),

    create: (data: CreateSubjectivityRequest) =>
      post<Subjectivity>('/subjectivities', data),

    update: (id: string, data: UpdateSubjectivityRequest) =>
      put<Subjectivity>(`/subjectivities/${id}`, data),

    cancel: (id: string) => del<void>(`/subjectivities/${id}`),

    getExpiringSoon: (params?: ExpiringSubjectivitiesParams) => {
      const searchParams = new URLSearchParams();
      if (params?.days) searchParams.append('days', String(params.days));
      if (params?.policy_id) searchParams.append('policy_id', params.policy_id);
      if (params?.type) searchParams.append('type', params.type);
      const query = searchParams.toString();
      return get<ExpiringSubjectivitiesResponse>(`/subjectivities/expiring-soon${query ? `?${query}` : ''}`);
    },

    submitEvidence: (id: string, data: SubmitEvidenceRequest) =>
      post<SubjectivityActionResponse>(`/subjectivities/${id}/submit-evidence`, data),

    satisfy: (id: string, request?: SatisfySubjectivityRequest) =>
      post<SubjectivityActionResponse>(`/subjectivities/${id}/satisfy`, request || {}),

    waive: (id: string, data: WaiveSubjectivityRequest) =>
      post<SubjectivityActionResponse>(`/subjectivities/${id}/waive`, data),

    addCondition: (id: string, data: { label: string; description?: string; type: 'photo' | 'document' }) =>
      post<SubjectivityCondition>(`/subjectivities/${id}/conditions`, data),

    countsByPolicy: () =>
      get<{ data: SubjectivityCountByPolicy[] }>('/subjectivities/counts-by-policy'),
  },

  // --------------------------------------------------------------------------
  // Mitigations
  // --------------------------------------------------------------------------
  mitigations: {
    list: (params?: MitigationListParams) =>
      get<MitigationListResponse>('/mitigations', { params: params as Record<string, string | number | boolean | undefined> }),
  },

  // --------------------------------------------------------------------------
  // Policy Actions (Underwriter Workflows)
  // --------------------------------------------------------------------------
  policyActions: {
    approve: async (policyId: string, notes?: string) => {
      const response = await request<{ success: boolean; status: string; policyId: string }>(
        '/policy-actions/approve',
        { method: 'POST', body: JSON.stringify({ policyId, notes }) }
      );
      return response;
    },
    requestRemediation: async (findingId: string, notes: string) => {
      const response = await request<{ success: boolean; findingId: string; status: string }>(
        '/policy-actions/remediation',
        { method: 'POST', body: JSON.stringify({ findingId, notes }) }
      );
      return response;
    },
    requestReUpload: async (findingId: string, reason: string, photoId?: string) => {
      const response = await request<{ success: boolean; findingId: string; reason: string; archivedPhotoId: string | null }>(
        '/policy-actions/re-upload',
        { method: 'POST', body: JSON.stringify({ findingId, reason, photoId }) }
      );
      return response;
    },
  },
  // --------------------------------------------------------------------------
  // Secure Links
  // --------------------------------------------------------------------------
  secureLinks: {
    list: (params?: Record<string, string | number | boolean | undefined>) =>
      get<any>('/secure-links', { params }),

    create: (data: { policyId: string; expiresInDays?: number; findingId?: string }) =>
      post<any>('/secure-links', {
        policy_id: data.policyId,
        expires_in_days: data.expiresInDays ?? 14,
        ...(data.findingId ? { finding_id: data.findingId } : {}),
      }),

    revoke: (uuid: string) =>
      post<void>(`/secure-links/${uuid}/revoke`),
  },

  // --------------------------------------------------------------------------
  // Uploads / Secure Links
  // --------------------------------------------------------------------------
  uploads: {
    validateLink: async (uuid: string): Promise<ValidateSecureLinkResponse> => {
      // Custom handler: always return JSON body regardless of HTTP status.
      // Expired (410), invalid (404), and revoked (403) links return useful
      // data in the response body that the UI needs to display.
      const url = `${API_BASE_URL}/secure-links/${uuid}/validate`;
      try {
        const response = await fetch(url);
        return await response.json();
      } catch {
        return { valid: false, status: 'error', error: 'Network error. Please check your connection.' };
      }
    },

    uploadPhoto: async (
      uuid: string,
      file: File,
      type: PhotoType,
      findingId?: string,
      onProgress?: (event: { loaded: number; total: number }) => void,
      thumbnail?: Blob | null
    ): Promise<UploadPhotoResponse> => {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('type', type);
      if (findingId) {
        formData.append('finding_id', findingId);
      }
      if (thumbnail) {
        formData.append('thumbnail', thumbnail, 'thumbnail.webp');
      }

      // Use XMLHttpRequest for progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress({ loaded: event.loaded, total: event.total });
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new APIClientError(
              'Upload failed',
              xhr.status,
              'UPLOAD_ERROR'
            ));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new APIClientError(
            'Network error during upload',
            0,
            'NETWORK_ERROR'
          ));
        });

        xhr.open('POST', `${API_BASE_URL}/uploads/${uuid}`);
        xhr.send(formData);
      });
    },

    getPhotos: (policyId: string) =>
      get<Photo[]>(`/policies/${policyId}/photos`),

    /**
     * Submit completed checklist for review.
     * POST /upload/:uuid/submit
     */
    submitChecklist: (uuid: string) =>
      post<{
        success: boolean;
        message: string;
        submissionDetails: {
          policyId: string;
          submittedAt: string;
          reviewTimeline: string;
          broker: { email: string; name: string } | null;
        };
      }>(`/upload/${uuid}/submit`, undefined, { skipAuth: true }),

    /**
     * Get checklist with progress for a secure link.
     * GET /uploads/:uuid/checklist
     */
    getChecklist: (uuid: string) =>
      get<any>(`/uploads/${uuid}/checklist`, { skipAuth: true }),

    /**
     * Get uploaded photos for a secure link.
     * GET /upload/:uuid/photos
     */
    getUploadedPhotos: (uuid: string) =>
      get<{ data: Photo[] }>(`/upload/${uuid}/photos`, { skipAuth: true }),
  },

  // --------------------------------------------------------------------------
  // Dashboard / Stats
  // --------------------------------------------------------------------------
  dashboard: {
    getStats: () => get<DashboardStats>('/dashboard/stats'),

    getFireZones: () => get<FireZone[]>('/fire-zones'),

    getFireZone: (id: string) => get<FireZone>(`/fire-zones/${id}`),

    // Portfolio Dashboard Stats (Phase 2)
    getPortfolioStats: () => get<PortfolioDashboardStats>('/dashboard/stats'),

    // Submission Readiness (Wave 1.1)
    getSubmissionReadiness: () =>
      get<import('../types/api').SubmissionReadinessResponse>('/dashboard/submission-readiness'),

    // Mitigation Summary (Epic 4B)
    getMitigationSummary: () =>
      get<MitigationSummaryResponse>('/dashboard/mitigation-summary'),

    // Inspector Stats (Wave 2.1)
    getInspectorStats: () =>
      get<{
        inspections: { total: number; completed: number; inProgress: number; assigned: number };
        impact: { findingsCreated: number; escalatedToSubjectivity: number; excepted: number };
        last_updated: string;
      }>('/dashboard/inspector-stats'),

    // Activity Stream
    getActivity: (limit?: number) =>
      get<{ activities: Array<{ id: string; event_type: string; description: string; timestamp: string; user_email?: string; resource_type?: string; resource_id?: string }> }>('/dashboard/activity', {
        params: limit ? { limit: String(limit) } : undefined,
      }),
  },

  // --------------------------------------------------------------------------
  // Photos
  // --------------------------------------------------------------------------
  photos: {
    list: (policyId: string, params?: PhotoListParams) =>
      get<PhotoListResponse>(`/policies/${policyId}/photos`, {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    get: (id: string) => get<Photo>(`/photos/${id}`),

    update: (id: string, data: { photo_type?: string }) =>
      patch<Photo>(`/photos/${id}`, data),

    delete: (id: string) => del<void>(`/photos/${id}`),

    /** Upload photo directly to Platform via policy page */
    upload: (policyId: string, file: File, photoType?: string, inspectionType?: string, thumbnail?: Blob | null) => {
      const formData = new FormData();
      formData.append('file', file);
      if (photoType) formData.append('photo_type', photoType);
      if (inspectionType) formData.append('inspection_type', inspectionType);
      if (thumbnail) formData.append('thumbnail', thumbnail, 'thumbnail.webp');
      return post<{ id: string; platformPhotoId: string; url: string; thumbnailUrl: string; urls: any; uploadedAt: string }>(
        `/policies/${policyId}/photos`,
        formData,
      );
    },

    linkFinding: (photoId: string, findingId: string) =>
      post<void>(`/photos/${photoId}/link-finding`, { finding_id: findingId }),

    unlinkFinding: (photoId: string, findingId: string) =>
      del<void>(`/photos/${photoId}/link-finding/${findingId}`),

    linkException: (photoId: string, exceptionId: string) =>
      post<void>(`/photos/${photoId}/link-exception`, { exception_id: exceptionId }),

    unlinkException: (photoId: string, exceptionId: string) =>
      del<void>(`/photos/${photoId}/link-exception/${exceptionId}`),

    // Photo Search (cross-policy search)
    search: (params: {
      address?: string;
      policy_number?: string;
      limit?: number;
      offset?: number;
    }) =>
      get<{
        data: any[];
        total: number;
        limit: number;
        offset: number;
      }>('/photos/search', {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    // Portfolio Analytics
    getPortfolioHazards: (params?: PortfolioAnalyticsParams) =>
      get<PortfolioAnalytics>('/photos/portfolio/hazards', {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    // Evidence Packet Generation
    generateEvidencePacket: (photoIds?: string[], policyId?: string) =>
      post<{
        photos: Array<{ id: string; filename: string; url: string; photoType: string; dateTaken: string | null }>;
        generatedAt: string;
      }>('/photos/evidence-packet', { photoIds, policyId }),
  },

  // --------------------------------------------------------------------------
  // Wildfire Real-Time Updates
  // --------------------------------------------------------------------------
  wildfire: {
    /**
     * Get fire updates since a given timestamp
     * @param since ISO 8601 timestamp
     * @param region Optional region filter (e.g., 'CA', 'OR')
     * @param limit Max number of updates to return (default: 100, max: 500)
     */
    getUpdates: async (since: string, region?: string, limit?: number) => {
      const params: Record<string, string | number> = { since };
      if (region) params.region = region;
      if (limit) params.limit = limit;

      // Build query string
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });

      // Use absolute URL to wildfire service (bypasses Bacchus API routing)
      const wildfireBaseUrl = getWildfireBaseUrl();
      const url = `${wildfireBaseUrl}/updates?${searchParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<FireUpdatesResponse>;
    },

    /**
     * Get summary of recent fire update activity
     * @param minutes Number of minutes to look back (default: 60, max: 1440)
     * @param region Optional region filter
     */
    getUpdatesSummary: async (minutes?: number, region?: string) => {
      const params: Record<string, string | number> = {};
      if (minutes) params.minutes = minutes;
      if (region) params.region = region;

      // Build query string
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });

      // Use absolute URL to wildfire service
      const wildfireBaseUrl = getWildfireBaseUrl();
      const queryString = searchParams.toString();
      const url = `${wildfireBaseUrl}/updates/summary${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<FireUpdatesSummaryResponse>;
    },

    // Historical fire data methods
    async getHistoricalFires(params?: HistoricalFiresParams): Promise<HistoricalPaginatedResponse<HistoricalFireEvent>> {
      // Build query string
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
      }

      // Use absolute URL to wildfire service (bypasses Bacchus API routing)
      const wildfireBaseUrl = getWildfireBaseUrl();
      const queryString = searchParams.toString();
      const url = `${wildfireBaseUrl}/fires${queryString ? '?' + queryString : ''}`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<HistoricalPaginatedResponse<HistoricalFireEvent>>;
    },

    async getHistoricalFire(fireId: string): Promise<HistoricalFireEvent> {
      // Use absolute URL to wildfire service
      const wildfireBaseUrl = getWildfireBaseUrl();
      const url = `${wildfireBaseUrl}/fires/${fireId}`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<HistoricalFireEvent>;
    },

    async getFirePerimeters(fireId: string, params?: HistoricalPerimetersParams): Promise<HistoricalFirePerimeter[]> {
      // Build query string
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
      }

      // Use absolute URL to wildfire service
      const wildfireBaseUrl = getWildfireBaseUrl();
      const queryString = searchParams.toString();
      const url = `${wildfireBaseUrl}/fires/${fireId}/perimeters${queryString ? '?' + queryString : ''}`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      const json = await response.json() as { data: HistoricalFirePerimeter[] };
      return json.data;
    },

    async getFireTimeline(fireId: string): Promise<HistoricalTimelineEvent[]> {
      // Use absolute URL to wildfire service
      const wildfireBaseUrl = getWildfireBaseUrl();
      const url = `${wildfireBaseUrl}/fires/${fireId}/timeline`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<HistoricalTimelineEvent[]>;
    },

    async getHistoricalHotspots(params?: HistoricalHotspotsParams): Promise<HistoricalPaginatedResponse<HistoricalFireHotspot>> {
      // Build query string
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
      }

      // Use absolute URL to wildfire service
      const wildfireBaseUrl = getWildfireBaseUrl();
      const queryString = searchParams.toString();
      const url = `${wildfireBaseUrl}/hotspots${queryString ? '?' + queryString : ''}`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let message = `Wildfire API error: ${response.status}`;
        let code = 'WILDFIRE_ERROR';
        try {
          const body = await response.json() as Record<string, unknown>;
          if (typeof body.message === 'string') message = body.message;
          if (typeof body.code === 'string') code = body.code;
        } catch { /* ignore parse errors */ }
        throw new APIClientError(message, response.status, code);
      }

      return response.json() as Promise<HistoricalPaginatedResponse<HistoricalFireHotspot>>;
    },

    // === WORKER C: Enhanced wildfire API methods ===

    async getSeverity(fireId: string): Promise<FireSeverityResponse> {
      // Use absolute URL to wildfire service
      const wildfireBaseUrl = API_BASE_URL.replace(/\/v1\/bacchus.*$/, '/v1/wildfire');
      const url = `${wildfireBaseUrl}/fires/${fireId}/severity`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Wildfire API error: ${response.status}`);
      }

      return response.json() as Promise<FireSeverityResponse>;
    },

    async getEvacuations(fireId: string, date?: string): Promise<FireEvacuationResponse> {
      // Build query string
      const searchParams = new URLSearchParams();
      if (date) {
        searchParams.append('date', date);
      }

      // Use absolute URL to wildfire service
      const wildfireBaseUrl = API_BASE_URL.replace(/\/v1\/bacchus.*$/, '/v1/wildfire');
      const queryString = searchParams.toString();
      const url = `${wildfireBaseUrl}/fires/${fireId}/evacuations${queryString ? '?' + queryString : ''}`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Wildfire API error: ${response.status}`);
      }

      return response.json() as Promise<FireEvacuationResponse>;
    },

    async getLosses(fireId: string): Promise<FireLossResponse> {
      // Use absolute URL to wildfire service
      const wildfireBaseUrl = API_BASE_URL.replace(/\/v1\/bacchus.*$/, '/v1/wildfire');
      const url = `${wildfireBaseUrl}/fires/${fireId}/losses`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Wildfire API error: ${response.status}`);
      }

      return response.json() as Promise<FireLossResponse>;
    },

    async getAnalysis(fireId: string): Promise<FireAnalysisResponse> {
      // Use absolute URL to wildfire service
      const wildfireBaseUrl = API_BASE_URL.replace(/\/v1\/bacchus.*$/, '/v1/wildfire');
      const url = `${wildfireBaseUrl}/fires/${fireId}/analysis`;

      const token = tokenManager.getToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Wildfire API error: ${response.status}`);
      }

      return response.json() as Promise<FireAnalysisResponse>;
    },

    // === END WORKER C ===
  },

  // --------------------------------------------------------------------------
  // Inspections
  // --------------------------------------------------------------------------
  inspections: {
    list: (params?: Record<string, string | number | boolean | undefined>) =>
      get<any>('/inspections', { params }),

    get: (id: string) => get<any>(`/inspections/${id}`),

    create: (data: any) => post<any>('/inspections', data),

    update: (id: string, data: any) => put<any>(`/inspections/${id}`, data),

    complete: (id: string) => post<any>(`/inspections/${id}/complete`),

    getPhotos: (id: string) => get<any>(`/inspections/${id}/photos`),

    getFindings: (id: string) => get<any>(`/inspections/${id}/findings`),
  },

  // --------------------------------------------------------------------------
  // Inspection Jobs
  // --------------------------------------------------------------------------
  inspectionJobs: {
    list: (params?: Record<string, string | number | boolean | undefined>) =>
      get<any>('/inspection-jobs', { params }),

    get: (id: string) => get<any>(`/inspection-jobs/${id}`),

    create: (data: any) => post<any>('/inspection-jobs', data),

    assign: (id: string, partnerId: string) =>
      post<any>(`/inspection-jobs/${id}/assign`, { partnerId }),

    updateStatus: (id: string, status: string) =>
      post<any>(`/inspection-jobs/${id}/status`, { status }),

    complete: (id: string) => post<any>(`/inspection-jobs/${id}/complete`),

    getPending: () => get<any>('/inspection-jobs/pending'),

    getOverdue: () => get<any>('/inspection-jobs/overdue'),
  },

  // --------------------------------------------------------------------------
  // Inspection Dispatch (Bacchus → wra-poc bridge)
  // --------------------------------------------------------------------------
  // Dispatch queue item type (from GET /dispatch/queue)
  // Defined here to avoid circular import with types/api.ts

  dispatch: {
    /** Dispatch a new broker-initiated inspection */
    create: (data: { policyId: string; protocolId?: string }) =>
      post<{ dispatchId: string; inspectionId: string; uuid: string; captureUrl: string; expiresAt: string }>(
        '/dispatch',
        data,
      ),

    /** Dispatch a reinspection scoped to findings */
    reinspect: (data: {
      policyId: string;
      findingIds: string[];
      parentWraInspectionId: string;
      protocolId?: string;
    }) =>
      post<{ dispatchId: string; inspectionId: string; uuid: string; captureUrl: string; expiresAt: string }>(
        '/dispatch/reinspect',
        data,
      ),

    /** Dispatch a contractor inspection */
    contractor: (data: {
      policyId: string;
      contractorId: string;
      findingIds?: string[];
      protocolId?: string;
    }) =>
      post<{ dispatchId: string; inspectionId: string; uuid: string; captureUrl: string; expiresAt: string }>(
        '/dispatch/contractor',
        data,
      ),

    /** Get active dispatch status for a policy */
    getActive: (policyId: string) =>
      get<{
        active: boolean;
        dispatchId?: string;
        status?: string;
        captureUrl?: string;
        wraInspectionId?: string;
      }>(`/dispatch/policy/${policyId}`),

    /** Broker review queue — list dispatches with policy details */
    queue: (status?: string) =>
      get<{ data: DispatchQueueItem[] }>('/dispatch/queue', {
        params: status ? { status } : undefined,
      }),

    /** Broker action on a dispatch (approve, request changes, etc.) */
    brokerAction: (dispatchId: string, action: string, reason?: string) =>
      post<{ dispatchId: string; brokerReviewStatus: string; reviewedAt: string }>(
        `/dispatch/${dispatchId}/broker-action`,
        { action, reason },
      ),
  },

  // --------------------------------------------------------------------------
  // Documents
  // --------------------------------------------------------------------------
  documents: {
    listByPolicy: (policyId: string, params?: import('../types/api').DocumentListParams) =>
      get<import('../types/api').DocumentListResponse>(`/policies/${policyId}/documents`, {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    listByFinding: (findingId: string) =>
      get<import('../types/api').DocumentListResponse>(`/findings/${findingId}/documents`),

    listByException: (exceptionId: string) =>
      get<import('../types/api').DocumentListResponse>(`/exceptions/${exceptionId}/documents`),

    listBySubjectivity: (subjectivityId: string) =>
      get<import('../types/api').DocumentListResponse>(`/subjectivities/${subjectivityId}/documents`),

    get: (id: string) => get<import('../types/api').DocumentMetadata & { linked_entities: any }>(`/documents/${id}`),

    upload: (policyId: string, file: File, documentType?: string, description?: string) => {
      const formData = new FormData();
      formData.append('file', file);
      if (documentType) formData.append('document_type', documentType);
      if (description) formData.append('description', description);
      return post<import('../types/api').DocumentUploadResponse>(
        `/policies/${policyId}/documents`,
        formData,
      );
    },

    download: async (id: string, filename?: string) => {
      const token = tokenManager.getToken();
      const url = `${API_BASE_URL}/documents/${id}/download`;
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'document';
      a.click();
      URL.revokeObjectURL(blobUrl);
    },

    preview: (id: string) => `${API_BASE_URL}/documents/${id}/preview`,

    linkToFinding: (documentId: string, findingId: string) =>
      post<void>(`/documents/${documentId}/link-finding`, { finding_id: findingId }),

    unlinkFromFinding: (documentId: string, findingId: string) =>
      del<void>(`/documents/${documentId}/link-finding/${findingId}`),

    linkToException: (documentId: string, exceptionId: string) =>
      post<void>(`/documents/${documentId}/link-exception`, { exception_id: exceptionId }),

    unlinkFromException: (documentId: string, exceptionId: string) =>
      del<void>(`/documents/${documentId}/link-exception/${exceptionId}`),

    linkToSubjectivity: (documentId: string, subjectivityId: string) =>
      post<void>(`/documents/${documentId}/link-subjectivity`, { subjectivity_id: subjectivityId }),

    unlinkFromSubjectivity: (documentId: string, subjectivityId: string) =>
      del<void>(`/documents/${documentId}/link-subjectivity/${subjectivityId}`),

    search: (params: import('../types/api').DocumentSearchParams) =>
      get<import('../types/api').DocumentSearchResponse>('/documents/search', {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    delete: (id: string) => del<void>(`/documents/${id}`),
  },

  // --------------------------------------------------------------------------
  // Renewals
  // --------------------------------------------------------------------------
  renewals: {
    list: () => get<any>('/renewals'),
  },

  // --------------------------------------------------------------------------
  // Renewal Inspections
  // --------------------------------------------------------------------------
  renewalInspections: {
    list: (params?: { policy_id?: string; status?: string; limit?: number; offset?: number }) =>
      get<import('../types/renewals').InspectionListResponse>('/renewal-inspections', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    get: (id: string) =>
      get<import('../types/renewals').RenewalInspectionDetail>(`/renewal-inspections/${id}`),

    getProgress: (id: string) =>
      get<import('../types/renewals').InspectionProgress>(`/renewal-inspections/${id}/progress`),

    transition: (id: string, to: string, guards?: Record<string, boolean>, extra?: { reason?: string; escalation_reason?: string }) =>
      post<import('../types/renewals').InspectionTransitionResult>(
        `/renewal-inspections/${id}/transition`,
        { to, ...(guards && { guards }), ...extra }
      ),
  },

  // --------------------------------------------------------------------------
  // Structures
  // --------------------------------------------------------------------------
  structures: {
    listByProperty: (propertyId: string) =>
      get<import('../types/renewals').StructureListResponse>('/structures', {
        params: { property_id: propertyId },
      }),

    get: (id: string) =>
      get<import('../types/renewals').Structure>(`/structures/${id}`),
  },

  // --------------------------------------------------------------------------
  // Video Walkarounds
  // --------------------------------------------------------------------------
  videoWalkarounds: {
    listByInspection: (inspectionId: string) =>
      get<import('../types/renewals').VideoListResponse>('/video-walkarounds', {
        params: { inspection_id: inspectionId },
      }),

    get: (id: string) =>
      get<import('../types/renewals').VideoWalkaroundDetail>(`/video-walkarounds/${id}`),
  },

  // --------------------------------------------------------------------------
  // Detail Photos
  // --------------------------------------------------------------------------
  detailPhotos: {
    listByInspection: (inspectionId: string, opts?: { structure_id?: string; tier?: string }) =>
      get<import('../types/renewals').DetailPhotoListResponse>('/detail-photos', {
        params: { inspection_id: inspectionId, ...opts } as Record<string, string | number | boolean | undefined>,
      }),

    reviewPhoto: (id: string, review_status: import('../types/renewals').PhotoReviewStatus, review_notes?: string | null) =>
      patch<import('../types/renewals').DetailPhoto>(`/detail-photos/${id}/review`, {
        review_status,
        ...(review_notes !== undefined && { review_notes }),
      }),
  },

  // --------------------------------------------------------------------------
  // Evidence Packets
  // --------------------------------------------------------------------------
  evidencePackets: {
    get: (inspectionId: string) =>
      get<import('../types/renewals').EvidencePacketResponse>(
        `/renewal-inspections/${inspectionId}/evidence-packet`
      ),

    generate: (inspectionId: string) =>
      post<import('../types/renewals').EvidencePacketResponse>(
        `/renewal-inspections/${inspectionId}/evidence-packet`
      ),

    listVersions: (inspectionId: string) =>
      get<{ inspection_id: string; versions: { id: string; version: number; generated_at: string; generated_by: string }[]; total: number }>(
        `/renewal-inspections/${inspectionId}/evidence-packet/versions`
      ),
  },

  // --------------------------------------------------------------------------
  // Admin Console (legacy Sprint 3A endpoints)
  // --------------------------------------------------------------------------
  admin: {
    getIntegrations: () => get<any>('/admin/integrations'),

    updateIntegration: (id: string, config: Record<string, string | number | boolean>) =>
      put<any>(`/admin/integrations/${id}`, config),

    testIntegration: (id: string) =>
      post<any>(`/admin/integrations/${id}/test`),

    getHealth: () => get<any>('/admin/health'),

    getLogs: (params?: { level?: string; dateRange?: string }) =>
      get<any>('/admin/logs', { params: params as Record<string, string | number | boolean | undefined> }),

    getUsers: () => get<any>('/admin/users'),

    createUser: (data: { email: string; firstName: string; lastName: string; role: string }) =>
      post<any>('/admin/users', data),

    disableUser: (id: string) =>
      put<any>(`/admin/users/${id}/disable`),

    resetPassword: (id: string) =>
      post<any>(`/admin/users/${id}/reset-password`),

    inviteUser: (data: { email: string; roleId: string; role: string }) =>
      post<{ success: boolean; waitlisted?: boolean; message: string }>('/admin/invitations', data),

    createIntegration: (data: { name: string; type: string; config: Record<string, string> }) =>
      post<any>('/admin/integrations', data),

    createAlertRule: (data: any) =>
      post<any>('/admin/alert-rules', data),

    getAlertRules: () => get<any>('/admin/alert-rules'),

    getKPIs: () => get<any>('/admin/kpis'),
  },

  // --------------------------------------------------------------------------
  // BMS Sprint 1 Stream D (G2) — Multi-Property Portfolio Dashboard
  // Mounted at /v1/admin/portfolio/* (admin role required).
  // --------------------------------------------------------------------------
  portfolio: {
    /** Top-level rollup: winery groups + orphan policies. */
    getRollup: (filters?: import('../types/portfolio').PortfolioFiltersRequest) =>
      get<import('../types/portfolio').PortfolioRollup>(
        '/admin/portfolio',
        { params: portfolioFiltersToParams(filters) }
      ),

    /** Aggregate counts + distributions for the scorecard. */
    getSummary: (filters?: import('../types/portfolio').PortfolioFiltersRequest) =>
      get<import('../types/portfolio').PortfolioSummary>(
        '/admin/portfolio/summary',
        { params: portfolioFiltersToParams(filters) }
      ),

    /** Filter+sort property list (table view). */
    listProperties: (params?: {
      filters?: import('../types/portfolio').PortfolioFiltersRequest;
      sortField?: import('../types/portfolio').PortfolioSortField;
      sortDirection?: import('../types/portfolio').PortfolioSortDirection;
    }) => get<import('../types/portfolio').PortfolioPropertyListResponse>(
      '/admin/portfolio/properties',
      {
        params: {
          ...portfolioFiltersToParams(params?.filters),
          sortField: params?.sortField,
          sortDirection: params?.sortDirection,
        },
      }
    ),

    /** Single-winery rollup (Stream G + H). */
    getWinery: (wineryAccountId: string) =>
      get<import('../types/portfolio').WinerySummary>(
        `/admin/portfolio/wineries/${wineryAccountId}`
      ),
  },

  // --------------------------------------------------------------------------
  // BMS Sprint 1 Stream C (G1) — Winery Accounts admin CRUD
  // Mounted at /v1/admin/winery-accounts/*.
  // --------------------------------------------------------------------------
  wineryAccounts: {
    list: (params?: { search?: string; limit?: number; offset?: number }) =>
      get<{ wineryAccounts: { id: string; wineryName: string }[]; total: number; limit: number; offset: number }>(
        '/admin/winery-accounts',
        { params: params as Record<string, string | number | boolean | undefined> }
      ),
    get: (id: string) =>
      get<{ wineryAccount: { id: string; wineryName: string } }>(
        `/admin/winery-accounts/${id}`
      ),
  },

  // --------------------------------------------------------------------------
  // RBAC: Users
  // --------------------------------------------------------------------------
  users: {
    list: (params?: { role?: string; status?: string; search?: string; limit?: number; offset?: number }) =>
      get<{ users: any[]; total: number; limit: number; offset: number }>('/users', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    get: (id: string) => get<any>(`/users/${id}`),

    create: (data: { email: string; role: string; displayName?: string }) =>
      post<any>('/users', data),

    update: (id: string, data: { email?: string; role?: string; displayName?: string | null; status?: string }) =>
      patch<any>(`/users/${id}`, data),

    delete: (id: string) => del<any>(`/users/${id}`),

    enable: (id: string) => post<any>(`/users/${id}/enable`),

    resendInvitation: (id: string) =>
      post<{ success: boolean; invitationSent: boolean }>(`/users/${id}/resend-invitation`),

    getActivity: (id: string, params?: { limit?: number; offset?: number }) =>
      get<{ entries: any[]; total: number; limit: number; offset: number }>(`/users/${id}/activity`, {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    getSessions: (id: string, params?: { limit?: number; offset?: number }) =>
      get<{ sessions: any[]; total: number; limit: number; offset: number }>(`/users/${id}/sessions`, {
        params: params as Record<string, string | number | boolean | undefined>,
      }),
  },

  // --------------------------------------------------------------------------
  // RBAC: Roles & Permissions
  // --------------------------------------------------------------------------
  roles: {
    list: () => get<{ roles: any[]; total: number }>('/roles'),

    get: (id: string) => get<any>(`/roles/${id}`),

    create: (data: { name: string; description?: string }) =>
      post<any>('/roles', data),

    update: (id: string, data: { name?: string; description?: string | null }) =>
      patch<any>(`/roles/${id}`, data),

    delete: (id: string) => del<any>(`/roles/${id}`),

    getPermissions: (id: string) =>
      get<{ permissions: any[]; total: number }>(`/roles/${id}/permissions`),

    grantPermission: (roleId: string, permissionId: string) =>
      post<any>(`/roles/${roleId}/permissions`, { permissionId }),

    revokePermission: (roleId: string, permissionId: string) =>
      del<any>(`/roles/${roleId}/permissions/${permissionId}`),

    getUsers: (id: string) =>
      get<{ users: any[]; total: number }>(`/roles/${id}/users`),
  },

  permissions: {
    list: () => get<{ permissions: any[] }>('/permissions'),
  },

  // --------------------------------------------------------------------------
  // RBAC: Partners
  // --------------------------------------------------------------------------
  partners: {
    list: (params?: { status?: string; type?: string; search?: string; limit?: number; offset?: number }) =>
      get<{ partners: any[]; total: number; limit: number; offset: number }>('/partners', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    get: (id: string) => get<any>(`/partners/${id}`),

    create: (data: { name: string; type: string; webhookUrl?: string; contactEmail?: string; contactPhone?: string }) =>
      post<any>('/partners', data),

    update: (id: string, data: { name?: string; webhookUrl?: string | null; contactEmail?: string | null; contactPhone?: string | null }) =>
      patch<any>(`/partners/${id}`, data),

    suspend: (id: string) => post<any>(`/partners/${id}/suspend`),

    activate: (id: string) => post<any>(`/partners/${id}/activate`),

    rotateApiKey: (id: string) => post<any>(`/partners/${id}/rotate-api-key`),
  },

  // --------------------------------------------------------------------------
  // Partner Domains (Gated Viral Identity)
  // --------------------------------------------------------------------------
  partnerDomains: {
    list: (params?: { search?: string; enabled?: boolean; limit?: number; offset?: number }) =>
      get<import('../types/identity').PartnerDomainListResponse>('/admin/partner-domains', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    get: (id: string) =>
      get<{ domain: import('../types/identity').PartnerDomain }>(`/admin/partner-domains/${id}`),

    create: (data: import('../types/identity').CreatePartnerDomainRequest) =>
      post<{ domain: import('../types/identity').PartnerDomain }>('/admin/partner-domains', data),

    update: (id: string, data: import('../types/identity').UpdatePartnerDomainRequest) =>
      put<{ domain: import('../types/identity').PartnerDomain }>(`/admin/partner-domains/${id}`, data),

    delete: (id: string) =>
      del<void>(`/admin/partner-domains/${id}`),

    disable: (id: string) =>
      post<{ success: boolean; users_suspended: number; invites_rejected: number }>(`/admin/partner-domains/${id}/disable`),

    enable: (id: string) =>
      post<{ success: boolean; users_reactivated: number }>(`/admin/partner-domains/${id}/enable`),

    getUsers: (id: string, params?: { limit?: number; offset?: number }) =>
      get<import('../types/identity').PartnerDomainUsersResponse>(`/admin/partner-domains/${id}/users`, {
        params: params as Record<string, string | number | boolean | undefined>,
      }),
  },

  // --------------------------------------------------------------------------
  // Pending Invites (Gated Viral Identity)
  // --------------------------------------------------------------------------
  pendingInvites: {
    list: (params?: { status?: string; domain?: string; limit?: number; offset?: number }) =>
      get<import('../types/identity').PendingInviteListResponse>('/admin/pending-invites', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    approve: (id: string) =>
      post<{ success: boolean; message: string }>(`/admin/pending-invites/${id}/approve`),

    reject: (id: string) =>
      post<{ success: boolean; message: string }>(`/admin/pending-invites/${id}/reject`),

    approveDomain: (domain: string) =>
      post<{ success: boolean; domain: string; invites_approved: number; approved_invites: any[] }>(
        '/admin/pending-invites/approve-domain',
        { domain },
      ),
  },

  // --------------------------------------------------------------------------
  // Surface Access (Gated Viral Identity)
  // --------------------------------------------------------------------------
  surfaceAccess: {
    getForUser: (userId: string) =>
      get<{ surfaces: import('../types/identity').UserSurfaceAccess[] }>(`/admin/users/${userId}/surfaces`),

    grant: (userId: string, surface: import('../types/identity').Surface) =>
      post<{ success: boolean }>(`/admin/users/${userId}/surfaces`, { surface }),

    revoke: (userId: string, surface: import('../types/identity').Surface) =>
      del<void>(`/admin/users/${userId}/surfaces/${surface}`),

    bulkGrantByDomain: (partnerDomainId: string, surface: import('../types/identity').Surface) =>
      post<{ success: boolean; users_granted: number }>(`/admin/partner-domains/${partnerDomainId}/grant-surface`, { surface }),
  },

  // --------------------------------------------------------------------------
  // Notifications
  // --------------------------------------------------------------------------
  notifications: {
    getPreferences: () =>
      get<{ preferences: any[] }>('/notifications/preferences'),

    updatePreferences: (data: any) =>
      post<{ message: string; preferences: any[] }>('/notifications/preferences', data),

    getHistory: (params?: { limit?: number; offset?: number }) =>
      get<{ history: any[]; total: number; limit: number; offset: number }>('/notifications/history', {
        params: params as Record<string, string | number | boolean | undefined>
      }),

    testEmail: (data: any) =>
      post<{ success: boolean; message: string }>('/notifications/test/email', data),

    testSMS: (data: any) =>
      post<{ success: boolean; message: string }>('/notifications/test/sms', data),
  },

  // --------------------------------------------------------------------------
  // Geocoding
  // --------------------------------------------------------------------------
  geocoding: {
    search: async (
      query: string,
      options?: { signal?: AbortSignal }
    ): Promise<{
      results: Array<{
        id: string;
        placeName: string;
        coordinates: [number, number];
        relevance: number;
        context: {
          city?: string;
          state?: string;
          country?: string;
          postcode?: string;
        };
      }>;
      attribution?: string;
    }> => {
      const url = `${API_BASE_URL}/geocode?q=${encodeURIComponent(query)}`;
      const token = tokenManager.getToken();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: options?.signal,
      });

      if (!response.ok) {
        const error: any = new Error('Geocoding failed');
        error.status = response.status;
        throw error;
      }

      return response.json();
    },

    /** Update policy coordinates after geocoding */
    updatePolicyCoordinates: (
      policyId: string,
      data: {
        latitude: number;
        longitude: number;
        geocodedAddress?: string;
      }
    ) =>
      patch<{
        success: boolean;
        policy: { id: string; latitude: number; longitude: number };
      }>(`/policies/${policyId}/coordinates`, data),
  },

  // --------------------------------------------------------------------------
  // Building Polygons
  // --------------------------------------------------------------------------
  buildings: {
    list: (params: BuildingListParams = {}) =>
      get<BuildingListResponse>('/buildings', { params: { ...params } }),

    getById: (id: string) =>
      get<{ building: BuildingPolygonResponse }>(`/buildings/detail/${id}`),

    submit: (data: BuildingSubmitRequest) =>
      post<BuildingPolygonResponse>('/buildings/submit', data),

    inBbox: (
      params: BuildingBboxParams,
      options?: { signal?: AbortSignal }
    ) =>
      get<{ polygons: BuildingPolygonResponse[]; total: number; truncated: boolean }>('/buildings/in-bbox', {
        params: { ...params },
        signal: options?.signal,
      }),

    byProperty: (propertyId: string) =>
      get<{ polygons: BuildingPolygonResponse[]; total: number }>(
        `/buildings/by-property/${propertyId}`
      ),

    completeness: (propertyId: string) =>
      get<BuildingCompletenessResponse>(
        `/buildings/completeness/${propertyId}`
      ),

    update: (id: string, data: BuildingUpdateRequest) =>
      put<BuildingPolygonResponse>(`/buildings/${id}`, data),

    review: (id: string, data: BuildingReviewRequest) =>
      put<BuildingPolygonResponse>(`/buildings/${id}/review`, data),

    delete: (id: string) =>
      del<{ success: boolean; id: string }>(`/buildings/${id}`),

    auditTrail: (buildingId: string) =>
      get<{ entries: AuditTrailEntry[]; total: number }>(`/buildings/${buildingId}/audit-trail`),

    history: (buildingId: string) =>
      get<{ history: any[]; total: number }>(`/buildings/${buildingId}/history`),

    /**
     * Fetch the HCI5×DS5 matrix + DS-5 zone aggregates for a building's most
     * recent renewal inspection. Returns matrix=null / ds5=null when VLM has
     * not run yet — never synthesised. Backed by GET
     * /v1/admin/buildings/:id/hci-ds-matrix (BMS Sprint 1 Stream L).
     */
    hciDsMatrix: (buildingId: string) =>
      get<{
        buildingId: string;
        propertyId: string | null;
        inspectionId: string | null;
        matrix: import('@ui/hci5xds5-matrix').HciDs5Matrix | null;
        ds5: import('@ui/ds-zones').DsZoneAssessment | null;
        dsScore: number | null;
      }>(`/admin/buildings/${buildingId}/hci-ds-matrix`),

    /**
     * Fetch the building's findings translated to wine-cargo language
     * (Stream B PR #1299 service). Returns one entry per finding with the
     * underwriter-facing wine-zone-prefixed risk code, inventory framing,
     * and per-status narrative. BMS Sprint 1 Stream L (UI wiring).
     */
    findingsTranslated: (buildingId: string) =>
      get<{
        buildingId: string;
        propertyId: string | null;
        inspectionId: string | null;
        findings: Array<{
          code: string;
          category: string;
          severity: string;
          title: string;
          description: string;
          status: 'compliant' | 'non_compliant';
          wine_cargo_risk_code: string;
          wine_cargo_narrative: string;
          inventory_at_risk_framing: string | null;
          wine_zone: string;
          mapping_known: boolean;
        }>;
      }>(`/admin/buildings/${buildingId}/findings-translated`),

    batchSubmit: (data: BatchSubmitBuildingRequest) =>
      post<BatchSubmitBuildingResponse>('/buildings/batch-submit', data),

    batchReview: (data: BatchReviewBuildingRequest) =>
      put<BatchReviewBuildingResponse>('/buildings/batch-review', data),

    photos: {
      list: (buildingId: string) =>
        get<{ photos: BuildingPhotoResponse[] }>(`/buildings/${buildingId}/photos`),

      upload: (buildingId: string, formData: FormData) =>
        post<{ photo: BuildingPhotoResponse }>(`/buildings/${buildingId}/photos`, formData),

      delete: (buildingId: string, photoId: string) =>
        del<{ success: boolean; id: string }>(`/buildings/${buildingId}/photos/${photoId}`),

      updateLocation: (buildingId: string, photoId: string, data: { gps_latitude: number; gps_longitude: number; compass_heading?: number | null }) =>
        patch<{ photo: BuildingPhotoResponse }>(`/buildings/${buildingId}/photos/${photoId}`, data),
    },
  },

  // --------------------------------------------------------------------------
  // VAR Replay
  // --------------------------------------------------------------------------
  var: {
    getReplayFires: () =>
      get<{ data: Array<{ slug: string; name: string; year: number; region: string }> }>('/var/replay/fires')
        .then(res => res.data),

    getReplay: (slug: string) =>
      get<{
        fire: { id: string; name: string; year: number; start_date: string | null; end_date: string | null; acres_burned: number | null };
        frames: Array<{ date: string; perimeter: GeoJSON.Geometry; acres: number; containment_pct: number | null; is_final: boolean; var_snapshot: { total_var: number; policies_at_risk: number; policies_in_perimeter: number; exposure_by_policy: Array<{ policy_id: string; policy_name: string; tiv: number; distance_miles: number; exposure_pct: number; exposure_usd: number }> } }>;
        portfolio: { total_policies: number; total_tiv: number };
      }>(`/var/replay/${encodeURIComponent(slug)}`),
  },

  // --------------------------------------------------------------------------
  // Claims / FNOL
  // --------------------------------------------------------------------------
  claims: {
    list: (params?: { status?: string; policy_id?: string }) =>
      get<import('../types/api').ClaimListResponse>('/claims', { params: params as Record<string, string | number | boolean | undefined> }),

    get: (id: string) => get<import('../types/api').Claim>(`/claims/${id}`),

    create: (data: import('../types/api').CreateClaimRequest) =>
      post<import('../types/api').Claim>('/claims', data),

    update: (id: string, data: Partial<import('../types/api').Claim>) =>
      patch<import('../types/api').Claim>(`/claims/${id}`, data),

    stats: () => get<import('../types/api').ClaimStats>('/claims/stats'),
  },

  // --------------------------------------------------------------------------
  // Admin Thumbnail Backfill
  // --------------------------------------------------------------------------
  adminBackfill: {
    getPhotosWithoutThumbnails: () =>
      get<{ photos: Array<{ id: string; imageUrl: string }>; total: number }>('/admin/photos-without-thumbnails'),

    uploadThumbnail: (photoId: string, thumbnail: Blob) => {
      const formData = new FormData();
      formData.append('thumbnail', thumbnail, 'thumbnail.webp');
      return post<{ id: string; thumbnail_r2_key: string }>(
        `/admin/photos/${photoId}/thumbnail`,
        formData,
      );
    },
  },
};

export default api;
